/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import ContextNode from './contextnode';
import type { NodeProps } from './types';

class TreeNode {
  readonly type: string;
  readonly name: string;
  public value: any;
  public previousValue: any;

  parent: ContextNode | TreeNode | null;

  readonly children: TreeNode[];
  private readonly eventSubscribers: ((object: { type: string }) => void)[];

  public readonly references: Map<string, TreeNode>;
  public props: NodeProps;

  constructor(type: string, name: string, options: NodeProps = {}) {
    this.type = type;
    this.name = name;
    this.parent = null;
    this.children = [];
    this.eventSubscribers = [];
    this.value = '';
    this.previousValue = '|~#-old-#~|';
    this.props = options;

    /**
     * {
     * "<ref_name>": <node_ref>
     * }
     * eq.,
     * {
     * "TestView": TreeNode
     * }
     *
     */
    this.references = new Map();
  }

  /**
   * @description This method adds the node to the set of references present in the current node
   * with the name as its key
   * @example <caption>Example for addReference()</caption>
   * // In this example, the API adds the reference to the current node
   * this.addReference("TOP_EmbeddedData_5.EmbeddedData",fieldNode)
   * // returns  -
   * @param name name of the reference to be added
   * @param node node to be added as a reference
   * @function
   * @private
   */
  addReference(name: string, node: TreeNode): void {
    this.references.set(name, node);
  }

  /**
   * @description This method gives the reference for name provided.
   * @example <caption>Example for getReference()</caption>
   * // In this example, the API gives the reference to the node we are looking for
   * viewNode = pageNode.getReference(parentView)
   * // returns  - Tree node which was set as reference
   * @param {string} name nameof node
   * @param name nameof node
   * @returns the reference node based on the name
   * @function
   * @private
   */
  getReference(name: string | undefined): this | undefined {
    return name ? (this.references.get(name) as this) : undefined;
  }

  /**
   * @description This method gives the node for type, name provided.
   * @example <caption>Example for getNode()</caption>
   * // In this example, the API gives the node for the type, name are looking for
   * node = this.getContextNode(contextName)?.getNode('PAGE', pageName)
   * // returns  - The node based on type, name provided
   * @param type type of node
   * @param name name of node
   * @returns - The node based on type, name provided
   * @function
   * @private
   */
  getNode(type: string, name: string, index?: null | number): this | undefined {
    return this.children.find(
      (node: TreeNode) => node.type === type && node.name === name && node.props.index === index
    ) as this;
  }

  /**
   * @description This method adds the node for type, name provided.
   * @example <caption>Example for addNode()</caption>
   * // In this example, the API adds the node for the type, name are looking for
   * const pageNode = this.addContextNode(contextName).addNode('PAGE', pageName);
   * // returns  - The node based on type, name provided
   * @param type type of node
   * @param name name of node
   * @returns - The added node based on type, name provided
   * @function
   * @private
   */
  addNode(type: string, name: string, options: NodeProps = {}): this {
    let node = this.getNode(type, name, options.index);
    if (node) {
      node.props = options;
    } else {
      node = new TreeNode(type, name, options) as this;
      node.parent = this;
      this.children.push(node);
    }
    return node;
  }

  /**
   * @description This method adds the evenlistener for callback provided.
   * @example <caption>Example for addEventListener()</caption>
   * // In this example, the API adds the callback to the event subscribers
   * this.getPageListNode(contextName, pageName, viewName, pageListName)?.addEventListener(callback)
   * // returns  -
   * @param callback function to be called on mutation
   * @function
   * @private
   */
  addEventListener(callback: (object: { type: string }) => void) {
    this.eventSubscribers.push(callback);
  }

  /**
   * @description This method triggers the event for the event object provided.
   * @example <caption>Example for triggerEvent()</caption>
   * // In this example, the API triggers the event for the event object
   * contextNode.getReference(`${pageName}.${pageListName}`)?.triggerEvent(mutateObject);
   * @param eventObject mutation objects to be passed to the listeners
   * @param stopPropagation stop propagating the event to parent
   * @function
   * @private
   */
  triggerEvent(eventObject: { type: string }, stopPropagation: boolean = false) {
    this.eventSubscribers.forEach((callback: (object: { type: string }) => void) => {
      callback(eventObject);
    });
    if (!stopPropagation && this.parent) {
      this.parent.triggerEvent(eventObject);
    }
  }

  setPreviousDefaultValue() {
    if (this.previousValue === '|~#-old-#~|') {
      this.previousValue = this.value;
    }
  }

  removeReference(name: string, index?: number) {
    const reference = this.references.get(name);

    if (reference?.props?.index === index) {
      this.references.delete(name);
    }
  }

  /**
   * This API removes the specified TreeNode from the children array of the current TreeNode.
   * @param name - name of the name of the pageList node
   * @private
   */
  removeChildNode(name: string): void {
    const index = this.children.findIndex((node: TreeNode) => node.name.startsWith(name));
    if (index !== -1) {
      this.children.splice(index, 1);
    }
  }

  /**
   * This API removes all the children from the current TreeNode.
   * @param name
   * @Private
   */
  removeChildren() {
    this.children.splice(0, this.children.length);
  }

  removeNode(name: string, index: number) {
    for (let i = this.children.length - 1; i >= 0; i -= 1) {
      const node = this.children[i];

      if (node.props.index === index && node.name === name) {
        this.children.splice(i, 1);
        break;
      }
    }
  }
}
export default TreeNode;
